import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AppUpdateService } from '@core/services/app-update.service';
import { map } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

export const authGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const appUpdateService = inject(AppUpdateService);

  return authService.isAuthenticated().pipe(
    map((authenticated) => {
      const hasPassword =
        !!AuthService.currentFirebaseUser?.reloadUserInfo?.passwordUpdatedAt &&
        !!AuthService.currentFirebaseUser?.reloadUserInfo?.passwordHash;
      const redirectingToCreatePassword = state.url.startsWith('/create-password');

      if (authenticated && !hasPassword && !redirectingToCreatePassword) {
        router.navigate(['/create-password']);

        return false;
      }

      // An already-authenticated user who already has a password (e.g. a stale bookmark, a back
      // button, or a link left over from before they set one) landing on /create-password has
      // nothing to do there - previously this fell all the way through to the unauthenticated
      // branch below and bounced a perfectly valid, logged-in user to the login page.
      if (authenticated && hasPassword && redirectingToCreatePassword) {
        router.navigate(['/']);

        return false;
      }

      if (authenticated && (!redirectingToCreatePassword || (redirectingToCreatePassword && !hasPassword))) {
        return true;
      }

      // Covers a session ending without ever going through AuthService.signOut() or the
      // interceptor's 401 handling (e.g. a token that's silently expired by the time the guard
      // re-checks it on navigation) - see AppUpdateService.clearImageDataGroupCache. A no-op,
      // idempotent call when there was nothing to clear (already-logged-out visitor, first-ever
      // visit), so it's safe to fire on every unauthenticated redirect rather than trying to
      // distinguish "was logged in" from "never was".
      void appUpdateService.clearImageDataGroupCache();
      router.navigate(['/auth/login'], {
        queryParams: { returnUrl: state.url === '/create-password' ? '/creator' : state.url },
      });

      return false;
    }),
  );
};
